import {
  type ConcurrentRequestsLimitation,
  type RateLimiterSettings,
  type ViptelaDataErrorHandlingSettings,
  arubaAuthTypes,
  defaultVendorApiSettings,
  settingTypes,
} from '@ipfabric/utils';
import type { ArubaNewCentralFormValues } from '../ArubaNewCentralForm';
import type { AwsFormValues } from '../AwsForm';
import type { AzureFormValues } from '../AzureForm';
import type { CheckPointFormValues } from '../CheckpointApiForm';
import type { CiscoApicFormValues } from '../CiscoApicForm';
import type { CiscoFmcFormValues } from '../CiscoFmcForm';
import type { ForcePointFormValues } from '../ForcePointForm';
import type { GCPFormValues } from '../GoogleCloudPlatformForm';
import type { JuniperMistFormValues } from '../JuniperMistForm';
import type { MerakiFormValues } from '../MerakiForm';
import type { NsxTFormValues } from '../NsxTForm';
import type { PrismaFormValues } from '../PrismaForm';
import type { RuckusFormValues } from '../RuckusForm';
import type { SilverPeakFormValues } from '../SilverPeakForm';
import type { VeloCloudFormValues } from '../VeloCloudForm';
import type { VersaFormValues } from '../VersaForm';
import type { ViptelaFormValues } from '../ViptelaForm';
import type { Vendor } from '../vendorTypes';
import { isRateLimiterSettings, isViptelaVendor } from './guardTypes';
import { isEmptyOrNullableField } from './isEmptyOrNullableField';

type RateLimitationSettings = RateLimiterSettings &
  ConcurrentRequestsLimitation;
type AdvancedSettings = RateLimitationSettings &
  ViptelaDataErrorHandlingSettings;

const getRateLimitedSettingsDefaultValuesFromExistingVendor = (
  vendor: Vendor & RateLimiterSettings,
  settings: RateLimiterSettings,
) => {
  return {
    refillRateIntervalMs:
      (!isEmptyOrNullableField(vendor.refillRateIntervalMs)
        ? vendor.refillRateIntervalMs
        : settings.refillRateIntervalMs) ?? 0,
    refillRate:
      (!isEmptyOrNullableField(vendor.refillRate)
        ? vendor.refillRate
        : settings.refillRate) ?? 0,
    maxCapacity:
      (!isEmptyOrNullableField(vendor.maxCapacity)
        ? vendor.maxCapacity
        : settings.maxCapacity) ?? 0,
  };
};

const getRateLimiterSettingsDefaultValues = (
  type: settingTypes.VendorApiType,
  vendor?: Vendor,
): Partial<RateLimiterSettings> => {
  const settings = defaultVendorApiSettings[type];
  if (isRateLimiterSettings(settings)) {
    if (vendor && isRateLimiterSettings(vendor)) {
      return getRateLimitedSettingsDefaultValuesFromExistingVendor(
        vendor,
        settings,
      );
    }

    return {
      refillRateIntervalMs: settings.refillRateIntervalMs ?? 0,
      refillRate: settings.refillRate ?? 0,
      maxCapacity: settings.maxCapacity ?? 0,
    };
  }

  return {};
};

export const getRateLimitationSettingsDefaultValues = (
  type: settingTypes.VendorApiType,
  vendor?: Vendor,
): Partial<RateLimitationSettings> => {
  return {
    maxConcurrentRequests:
      (!isEmptyOrNullableField(vendor?.maxConcurrentRequests)
        ? vendor?.maxConcurrentRequests
        : defaultVendorApiSettings[type].maxConcurrentRequests) ?? 0,
    ...getRateLimiterSettingsDefaultValues(type, vendor),
  };
};

export const getAdvancedSettingsDefaultValues = (
  vendor: Vendor,
): Partial<AdvancedSettings> => {
  return {
    ...getRateLimitationSettingsDefaultValues(vendor.type, vendor),
    ...(isViptelaVendor(vendor) && {
      unreachableDeviceMaxRetry: !isEmptyOrNullableField(
        vendor.unreachableDeviceMaxRetry,
      )
        ? vendor.unreachableDeviceMaxRetry
        : defaultVendorApiSettings[vendor.type].unreachableDeviceMaxRetry,
      unreachableDeviceBaseDelaySec: !isEmptyOrNullableField(
        vendor.unreachableDeviceBaseDelaySec,
      )
        ? vendor.unreachableDeviceBaseDelaySec
        : defaultVendorApiSettings[vendor.type].unreachableDeviceBaseDelaySec,
    }),
  };
};

export const defaultInitialValues = {
  [settingTypes.vendorApiNames.f5osa]: {
    type: settingTypes.vendorApiNames.f5osa,
    baseUrls: [],
    isEnabled: true,
    password: '',
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,
    username: '',

    comment: '',
    slug: '',
  },
  [settingTypes.vendorApiNames.f5osc]: {
    type: settingTypes.vendorApiNames.f5osc,
    baseUrls: [],
    isEnabled: true,
    password: '',
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,
    username: '',

    comment: '',
    slug: '',
  },
  [settingTypes.vendorApiNames.meraki]: {
    type: settingTypes.vendorApiNames.meraki,
    apiKey: '',
    apiVer: 'v1',
    baseUrl: 'https://api.meraki.com/api/',
    organizations: [],
    merakiFilters: {
      type: 'groupFilter' as const,
      operator: 'and' as const,
      filters: [],
    },
    isEnabled: true,
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,

    comment: '',
    slug: '',
  } as MerakiFormValues,
  [settingTypes.vendorApiNames.aws]: {
    type: settingTypes.vendorApiNames.aws,
    apiKey: '',
    apiSecret: '',
    assumeRoles: [],
    isEnabled: true,
    regions: ['us-east-1'],
    respectSystemProxyConfiguration: true,

    comment: '',
    slug: '',
  } as AwsFormValues,
  [settingTypes.vendorApiNames.checkpoint]: {
    type: settingTypes.vendorApiNames.checkpoint,
    apiKey: '',
    baseUrl: '',
    domains: [],
    isEnabled: true,
    password: '',
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,
    username: '',

    comment: '',
    slug: '',
  } as CheckPointFormValues,
  [settingTypes.vendorApiNames.silverpeak]: {
    type: settingTypes.vendorApiNames.silverpeak,
    apiKey: '',
    baseUrl: '',
    isEnabled: true,
    password: '',
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,
    username: '',
    loginType: 'Local',

    comment: '',
    slug: '',
  } as SilverPeakFormValues,
  [settingTypes.vendorApiNames.versa]: {
    type: settingTypes.vendorApiNames.versa,
    baseUrl: '',
    isEnabled: true,
    password: '',
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,
    username: '',

    comment: '',
    slug: '',
    paginationLimit:
      defaultVendorApiSettings[settingTypes.vendorApiNames.versa]
        .paginationLimit,
    combinedDiscovery: false,
  } as VersaFormValues,
  [settingTypes.vendorApiNames.viptela]: {
    type: settingTypes.vendorApiNames.viptela,
    baseUrl: '',
    isEnabled: true,
    password: '',
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,
    username: '',

    comment: '',
    slug: '',
    combinedDiscovery: false,
    unreachableDeviceBaseDelaySec:
      defaultVendorApiSettings[settingTypes.vendorApiNames.viptela]
        .unreachableDeviceBaseDelaySec,
    unreachableDeviceMaxRetry:
      defaultVendorApiSettings[settingTypes.vendorApiNames.viptela]
        .unreachableDeviceMaxRetry,
  } as ViptelaFormValues,
  [settingTypes.vendorApiNames.nsxT]: {
    type: settingTypes.vendorApiNames.nsxT,
    baseUrl: '',
    isEnabled: true,
    password: '',
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,
    username: '',

    comment: '',
    slug: '',
  } as NsxTFormValues,
  [settingTypes.vendorApiNames.ciscoapic]: {
    type: settingTypes.vendorApiNames.ciscoapic,
    baseUrl: '',
    isEnabled: true,
    password: '',
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,
    username: '',

    comment: '',
    slug: '',
  } as CiscoApicFormValues,
  [settingTypes.vendorApiNames.ciscofmc]: {
    type: settingTypes.vendorApiNames.ciscofmc,
    baseUrl: '',
    isEnabled: true,
    password: '',
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,
    username: '',
    apiToken: '',

    comment: '',
    slug: '',
  } as CiscoFmcFormValues,
  [settingTypes.vendorApiNames.prisma]: {
    type: settingTypes.vendorApiNames.prisma,
    isEnabled: true,
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,
    password: '',
    username: '',
    tsgid: '',

    comment: '',
    slug: '',
  } as PrismaFormValues,
  [settingTypes.vendorApiNames.azure]: {
    type: settingTypes.vendorApiNames.azure,
    clientId: '',
    clientSecret: '',
    isEnabled: true,
    respectSystemProxyConfiguration: true,
    subscriptionIds: [],
    tenantId: '',

    comment: '',
    slug: '',
  } as AzureFormValues,
  [settingTypes.vendorApiNames.ruckusvsz]: {
    type: settingTypes.vendorApiNames.ruckusvsz,
    apiVersion: 'v9_1',
    baseUrl: '',
    isEnabled: true,
    password: '',
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,
    username: '',

    comment: '',
    slug: '',
  } as RuckusFormValues,
  [settingTypes.vendorApiNames.junipermist]: {
    type: settingTypes.vendorApiNames.junipermist,
    apiToken: '',
    apiVersion: 'v1',
    baseUrl: 'https://api.mist.com',
    isEnabled: true,
    respectSystemProxyConfiguration: true,
    rejectUnauthorized: true,

    comment: '',
    slug: '',
  } as JuniperMistFormValues,
  [settingTypes.vendorApiNames.forcepoint]: {
    type: settingTypes.vendorApiNames.forcepoint,
    authenticationKey: '',
    baseUrl: '',
    isEnabled: true,
    respectSystemProxyConfiguration: true,
    rejectUnauthorized: true,

    comment: '',
    slug: '',
  } as ForcePointFormValues,
  [settingTypes.vendorApiNames.gcp]: {
    type: settingTypes.vendorApiNames.gcp,
    isEnabled: true,
    comment: '',
    slug: '',

    respectSystemProxyConfiguration: true,
    credentialsJson: '',
  } as GCPFormValues,
  [settingTypes.vendorApiNames.velocloud]: {
    type: settingTypes.vendorApiNames.velocloud,
    isEnabled: true,
    apiToken: '',
    baseUrl: '',
    username: '',
    password: '',
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,

    comment: '',
    slug: '',
  } as VeloCloudFormValues,
  [settingTypes.vendorApiNames.arubaNewCentral]: {
    type: settingTypes.vendorApiNames.arubaNewCentral,
    isEnabled: true,
    baseUrl: '',
    // Central by default (backward-compatible). GLP/MSP are cloud-only and only
    // add a Workspace ID; the GreenLake base URL + tenant list are backend-side.
    // Persisted in the shared `loginType` / `tenantId` columns.
    loginType: arubaAuthTypes.central,
    tenantId: '',
    clientId: '',
    clientSecret: '',
    rejectUnauthorized: true,
    respectSystemProxyConfiguration: true,

    comment: '',
    slug: '',
  } as ArubaNewCentralFormValues,
};
